import React, { useState, useEffect } from 'react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { Package, Search, Loader2 } from 'lucide-react';

interface Product {
  id: string;
  name: string;
  url: string;
  category?: string;
  price?: number;
  image?: string;
}

interface ProductPickerProps {
  siteId: string;
  onProductSelect: (product: Product) => void;
  selectedProduct?: Product | null;
}

export default function ProductPicker({ siteId, onProductSelect, selectedProduct }: ProductPickerProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProducts();
  }, [siteId]);

  const fetchProducts = async () => {
    if (!siteId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(
        `${API_URL}/growth/products?siteId=${siteId}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const data = await response.json();
      setProducts(data.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
        <span className="text-gray-600">Loading products...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchProducts}
          className="mt-2 text-sm text-blue-600 hover:text-blue-700"
        >
          Try again
        </button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Package className="h-12 w-12 mx-auto mb-3 text-gray-400" />
        <p>No products found on this site.</p>
        <p className="text-sm mt-1">Products are detected from pages with e-commerce characteristics.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Product Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
        {filteredProducts.map((product) => (
          <button
            key={product.id}
            onClick={() => onProductSelect(product)}
            className={`text-left p-4 border rounded-lg hover:shadow-md transition-all ${
              selectedProduct?.id === product.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start space-x-3">
              {product.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-16 h-16 object-cover rounded"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                  <Package className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 truncate">
                  {product.name}
                </h3>
                {product.category && (
                  <p className="text-sm text-gray-500 truncate">
                    {product.category}
                  </p>
                )}
                {product.price && (
                  <p className="text-sm font-medium text-gray-700">
                    ${product.price.toFixed(2)}
                  </p>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {filteredProducts.length === 0 && searchTerm && (
        <p className="text-center text-gray-500 py-4">
          No products match your search
        </p>
      )}
    </div>
  );
}